// ContextModal.jsx – gegroepeerde boomweergave (top-3 level-2 groepen per source) met inklappen
import { Fragment, useMemo, useCallback, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, BookOpen, FileText, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const GROUP_LEVEL = 2; // groepeer op level 2, bv. 12.1

// ---------- helpers ----------
function getChunkId(chunk) {
  return chunk?.id ? String(chunk.id) : `${chunk?.source || 'src'}::${chunk?.page || 'p'}::${chunk?.heading || 'h'}`;
}
function numberKeyFromHeading(heading) {
  if (!heading || typeof heading !== 'string') return [];
  const m = heading.trim().match(/^(\d+(?:\.\d+)*)(?!\S)/);
  const raw = m ? m[1] : (heading.match(/\d+(?:\.\d+)*/)?.[0] ?? '');
  if (!raw) return [];
  return raw.split('.').map(n => parseInt(n, 10));
}
function computeHeadingLevel(chunk) {
  if (chunk?.heading_level != null) {
    const n = Number(chunk.heading_level);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const parts = numberKeyFromHeading(chunk?.heading);
  return parts.length || 1;
}
function prefixAtLevel(heading, level = GROUP_LEVEL) {
  const parts = numberKeyFromHeading(heading);
  if (parts.length === 0) return null;
  if (parts.length <= level) return parts.join('.');
  return parts.slice(0, level).join('.');
}
function sortByHeadingNumber(a, b) {
  const ak = numberKeyFromHeading(a?.heading);
  const bk = numberKeyFromHeading(b?.heading);
  const len = Math.max(ak.length, bk.length);
  for (let i = 0; i < len; i++) {
    const av = ak[i] ?? -1;
    const bv = bk[i] ?? -1;
    if (av !== bv) return av - bv;
  }
  return (a?.heading || '').localeCompare(b?.heading || '');
}
function uniqueBy(arr, keyFn) {
  const seen = new Set();
  const out = [];
  for (const item of arr) {
    const k = keyFn(item);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
}

// Bouw boom per parent: alle children met parent_main_id === parent.id worden genest o.b.v. parent_id.
function buildTreeForParent(parent, allChildren) {
  const nodes = new Map(); // id -> {chunk, children: []}
  const ensure = (chunk) => {
    const id = getChunkId(chunk);
    if (!nodes.has(id)) nodes.set(id, { chunk, children: [] });
    return nodes.get(id);
  };

  ensure(parent);
  allChildren.forEach(c => ensure(c));

  allChildren.forEach((c) => {
    const cid = getChunkId(c);
    const parentId = c.parent_id ? String(c.parent_id) : null;
    const node = nodes.get(cid);

    if (parentId && nodes.has(parentId) && parentId !== cid) {
      nodes.get(parentId).children.push(node);
    } else {
      const rootId = getChunkId(parent);
      if (cid !== rootId) nodes.get(rootId).children.push(node);
    }
  });

  const sortRec = (node) => {
    node.children.sort((a, b) => sortByHeadingNumber(a.chunk, b.chunk));
    node.children.forEach(sortRec);
  };
  sortRec(nodes.get(getChunkId(parent)));

  return nodes.get(getChunkId(parent));
}

export function ContextModal({ isOpen, onClose, contexts = [] }) {
  const handleClose = useCallback(() => onClose(), [onClose]);

  // --------- 1) groepeer alle contextitems op (source, level-2 prefix) ----------
  const groups = useMemo(() => {
    const buckets = new Map(); // key -> {source, prefix, items: []}
    contexts.forEach((c) => {
      const pref = prefixAtLevel(c.heading, GROUP_LEVEL);
      if (!pref) return;
      const src = String(c.source || '');
      const key = `${src}::${pref}`;
      if (!buckets.has(key)) buckets.set(key, { source: src, prefix: pref, items: [] });
      buckets.get(key).items.push(c);
    });

    const groupsArr = [];
    for (const [key, bucket] of buckets.entries()) {
      const items = bucket.items;

      let parent = items.find(i => !i.derived_from_parent && computeHeadingLevel(i) === GROUP_LEVEL);
      if (!parent) {
        const nonDerived = items.filter(i => !i.derived_from_parent);
        if (nonDerived.length) {
          nonDerived.sort((a, b) => {
            const da = Math.abs(computeHeadingLevel(a) - GROUP_LEVEL);
            const db = Math.abs(computeHeadingLevel(b) - GROUP_LEVEL);
            return da - db || sortByHeadingNumber(a, b);
          });
          parent = nonDerived[0];
        }
      }
      if (!parent) {
        const sorted = [...items].sort((a, b) => {
          const da = Math.abs(computeHeadingLevel(a) - GROUP_LEVEL);
          const db = Math.abs(computeHeadingLevel(b) - GROUP_LEVEL);
          return da - db || sortByHeadingNumber(a, b);
        });
        parent = sorted[0];
      }

      const parentId = parent?.id ? String(parent.id) : null;
      const children = [];
      const seenChild = new Set();

      if (parentId) {
        items.forEach(i => {
          if (String(i.parent_main_id || '') === parentId) {
            const kidKey = getChunkId(i);
            if (!seenChild.has(kidKey)) {
              seenChild.add(kidKey);
              children.push(i);
            }
          }
        });
      }
      items.forEach(i => {
        const pref = prefixAtLevel(i.heading, GROUP_LEVEL);
        const lvl = computeHeadingLevel(i);
        if (pref === bucket.prefix && lvl > GROUP_LEVEL) {
          const kidKey = getChunkId(i);
          if (!seenChild.has(kidKey)) {
            seenChild.add(kidKey);
            children.push(i);
          }
        }
      });

      children.sort(sortByHeadingNumber);

      groupsArr.push({
        key,
        source: bucket.source,
        prefix: bucket.prefix,
        parent,
        children
      });
    }

    const firstIndex = new Map();
    contexts.forEach((c, idx) => {
      const pref = prefixAtLevel(c.heading, GROUP_LEVEL);
      const src = String(c.source || '');
      if (!pref) return;
      const k = `${src}::${pref}`;
      if (!firstIndex.has(k)) firstIndex.set(k, idx);
    });
    groupsArr.sort((a, b) => (firstIndex.get(a.key) ?? 0) - (firstIndex.get(b.key) ?? 0));

    return groupsArr;
  }, [contexts]);

  // --------- 2) toon top-3 groepen ----------
  const topGroups = useMemo(() => groups.slice(0, 3), [groups]);

  // --------- 3) bouw bomen per groep ----------
  const trees = useMemo(() => {
    const map = new Map();
    topGroups.forEach((g) => {
      if (!g.parent) return;
      map.set(g.key, buildTreeForParent(g.parent, g.children));
    });
    return map;
  }, [topGroups]);

  // UI state
  const [expandedNodes, setExpandedNodes] = useState(() => new Set()); // children zichtbaar
  const [openTextNodes, setOpenTextNodes] = useState(() => new Set()); // markdown zichtbaar

  // Klikcyclus: dicht -> children -> tekst -> dicht
  const toggleNode = useCallback((nodeId, hasChildren) => {
    setExpandedNodes(prevExp => {
      const expanded = new Set(prevExp);
      const isExpanded = expanded.has(nodeId);
      const showText = openTextNodes.has(nodeId);

      if (!isExpanded) {
        // stap 1: open children of tekst (als leaf)
        if (hasChildren) {
          expanded.add(nodeId);
          setOpenTextNodes(prevText => {
            const t = new Set(prevText);
            t.delete(nodeId);
            return t;
          });
        } else {
          setOpenTextNodes(prevText => {
            const t = new Set(prevText);
            t.has(nodeId) ? t.delete(nodeId) : t.add(nodeId);
            return t;
          });
        }
      } else {
        if (!showText) {
          // stap 2: node is expanded maar tekst nog niet → toon tekst
          setOpenTextNodes(prevText => {
            const t = new Set(prevText);
            t.add(nodeId);
            return t;
          });
        } else {
          // stap 3: tekst stond open → alles dicht
          expanded.delete(nodeId);
          setOpenTextNodes(prevText => {
            const t = new Set(prevText);
            t.delete(nodeId);
            return t;
          });
        }
      }
      return expanded;
    });
  }, [openTextNodes]);

  // Chevron: direct children open/dicht (zonder tekst te togglen)
  const setExpandedOnly = useCallback((nodeId, expand) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (expand) next.add(nodeId); else next.delete(nodeId);
      return next;
    });
    if (!expand) {
      setOpenTextNodes(prev => {
        const t = new Set(prev);
        t.delete(nodeId);
        return t;
      });
    }
  }, []);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95 translate-y-4" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-95 translate-y-4">
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl sm:rounded-3xl bg-white dark:bg-dark-800 shadow-2xl transition-all border border-gray-100 dark:border-dark-700/50">
                <div className="relative bg-gradient-to-r from-primary-50 via-white to-secondary-50 dark:from-primary-900/20 dark:via-dark-800 dark:to-secondary-900/20 p-6 sm:p-8 border-b border-gray-100 dark:border-dark-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center">
                        <BookOpen className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white">
                          Gebruikte bronnen
                        </Dialog.Title>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {Math.min(3, groups.length)} hoofdbronnen getoond
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200 flex items-center justify-center group"
                    >
                      <X className="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
                    </button>
                  </div>
                </div>

                <div
                  className="max-h-[60vh] sm:max-h-[70vh] overflow-y-auto scrollbar-thin"
                  style={{ overscrollBehavior: 'contain', willChange: 'transform', transform: 'translateZ(0)', WebkitOverflowScrolling: 'touch' }}
                >
                  {topGroups.length > 0 ? (
                    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                      {topGroups.map((g) => {
                        const tree = trees.get(g.key);
                        if (!tree) return null;
                        return (
                          <ParentTreeCard
                            key={g.key}
                            root={tree}
                            expandedNodes={expandedNodes}
                            openTextNodes={openTextNodes}
                            toggleNode={toggleNode}
                            setExpandedOnly={setExpandedOnly}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-dark-700 rounded-3xl flex items-center justify-center mx-auto mb-4">
                        <BookOpen className="h-8 w-8 text-gray-400 dark:text-gray-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Geen bronnen gevonden</h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">Er zijn geen bronnen beschikbaar voor dit bericht.</p>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// ---------- Cards / Tree rendering ----------
function ParentTreeCard({ root, expandedNodes, openTextNodes, toggleNode, setExpandedOnly }) {
  return (
    <div className="group relative" style={{ transform: 'translateZ(0)' }}>
      <div className="bg-gray-50/80 dark:bg-dark-900/50 rounded-2xl border border-gray-200/50 dark:border-dark-700/50 overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-primary-500/5 dark:hover:shadow-primary-400/5">
        <TreeRow node={root} isRoot expandedNodes={expandedNodes} openTextNodes={openTextNodes} toggleNode={toggleNode} setExpandedOnly={setExpandedOnly} />
        <div className="p-2 sm:p-3">
          <TreeChildren node={root} expandedNodes={expandedNodes} openTextNodes={openTextNodes} toggleNode={toggleNode} setExpandedOnly={setExpandedOnly} />
        </div>
      </div>
    </div>
  );
}

function TreeRow({ node, isRoot = false, expandedNodes, openTextNodes, toggleNode, setExpandedOnly }) {
  const chunk = node.chunk;
  const id = getChunkId(chunk);
  const isExpanded = expandedNodes.has(id);
  const showText = openTextNodes.has(id);
  const hasChildren = (node.children?.length || 0) > 0;

  const rootLabel = isRoot
    ? (prefixAtLevel(chunk.heading, GROUP_LEVEL) ? chunk.heading : chunk.heading)
    : chunk.heading;

  const openPdfAtPage = useCallback((source, page) => {
    if (!source || !page) return;
    try {
      const baseUrl = `https://chatbot.vps.matchperfect.nl/api/files/${encodeURIComponent(source)}`;
      const viewerBaseUrl = `${window.location.origin}/pdfjs/web/viewer.html`;
      const pdfViewerUrl = `${viewerBaseUrl}?file=${encodeURIComponent(baseUrl)}#page=${page}&zoom=page-fit`;
      window.open(pdfViewerUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Fout bij openen PDF:', error);
      const fallback = `https://chatbot.vps.matchperfect.nl/api/files/${encodeURIComponent(source)}`;
      window.open(fallback, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleOpenPdf = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    openPdfAtPage(chunk.source, chunk.page);
  }, [chunk.source, chunk.page, openPdfAtPage]);

  const onRowClick = useCallback(() => {
    toggleNode(id, hasChildren);
  }, [id, hasChildren, toggleNode]);

  // Chevron click: alleen children togglen (zonder tekst te beïnvloeden)
  const onChevronClick = useCallback((e) => {
    e.stopPropagation();
    setExpandedOnly(id, !isExpanded);
  }, [id, isExpanded, setExpandedOnly]);

  return (
    <div
      className={`bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm p-3 sm:p-4 border-b border-gray-100 dark:border-dark-700/50 ${isRoot ? '' : 'rounded-xl'} cursor-pointer`}
      onClick={onRowClick}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-gradient-to-br from-primary-100 to-secondary-100 dark:from-primary-900/30 dark:to-secondary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
          <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <h3 className="font-bold text-gray-900 dark:text-white text-sm sm:text-base leading-tight">
              {rootLabel}
            </h3>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs sm:text-sm">
            {chunk.page !== 1 && (
              <div className="flex items-center gap-2 bg-primary-50 dark:bg-primary-900/20 px-2.5 py-1 rounded-full">
                <div className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                <span className="font-medium text-primary-700 dark:text-primary-300">Pagina {chunk.page}</span>
              </div>
            )}
            <button
              onClick={handleOpenPdf}
              className="flex items-center gap-2 bg-gray-100 dark:bg-dark-700 px-2.5 py-1 rounded-full text-primary-700 dark:text-primary-300 font-medium underline hover:text-primary-900 dark:hover:text-primary-100 transition-colors"
              title="Open bestand op pagina"
            >
              {chunk.source}
            </button>
          </div>
        </div>

        <button
          onClick={onChevronClick}
          className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
          title={isExpanded ? 'Inklappen' : 'Uitklappen'}
        >
          <ChevronRight className={`h-5 w-5 text-gray-400 dark:text-gray-600 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90 text-primary-500 dark:text-primary-400' : ''}`} />
        </button>
      </div>

      {/* Tekst is standaard ingeklapt; 2e klik (of leaf) toont/verbergt tekst; 3e klik klapt weer alles in */}
      {showText && chunk.text && (
        <div className="mt-3">
          <div className="prose prose-sm sm:prose-base dark:prose-invert max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => <p className="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base" {...props} />,
                h1: ({ node, ...props }) => <h1 className="text-lg sm:text-xl font-bold mb-4 text-gray-900 dark:text-white" {...props} />,
                h2: ({ node, ...props }) => <h2 className="text-base sm:text-lg font-bold mb-3 text-gray-900 dark:text-white" {...props} />,
                h3: ({ node, ...props }) => <h3 className="text-sm sm:text-base font-bold mb-2 text-gray-900 dark:text-white" {...props} />,
                ul: ({ node, ...props }) => <ul className="mb-4 pl-5 space-y-2" {...props} />,
                ol: ({ node, ...props }) => <ol className="mb-4 pl-5 space-y-2" {...props} />,
                li: ({ node, ...props }) => <li className="text-gray-700 dark:text-gray-300 text-sm sm:text-base" {...props} />,
                strong: ({ node, ...props }) => <strong className="font-bold text-gray-900 dark:text-white" {...props} />,
                code: ({ node, inline, ...props }) =>
                  inline ? (
                    <code className="bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200 px-2 py-1 rounded-lg text-xs sm:text-sm font-mono border border-primary-200 dark:border-primary-800" {...props} />
                  ) : (
                    <code className="block bg-gray-100 dark:bg-dark-700 p-4 rounded-xl text-xs sm:text-sm font-mono overflow-x-auto border border-gray-200 dark:border-dark-600" {...props} />
                  ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto w-full mb-4">
                    <table className="min-w-full border border-gray-300 dark:border-dark-700 text-sm text-left" {...props} />
                  </div>
                ),
                thead: ({ node, ...props }) => <thead className="bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white" {...props} />,
                tbody: ({ node, ...props }) => <tbody className="divide-y divide-gray-200 dark:divide-dark-700" {...props} />,
                tr: ({ node, ...props }) => <tr className="border-b border-gray-200 dark:border-dark-700" {...props} />,
                th: ({ node, ...props }) => <th className="px-4 py-2 font-medium border border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-800 text-gray-800 dark:text-white" {...props} />,
                td: ({ node, ...props }) => <td className="px-4 py-2 border border-gray-300 dark:border-dark-700 text-gray-700 dark:text-gray-300" {...props} />,
                blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-primary-300 dark:border-primary-600 pl-4 py-2 mb-4 bg-primary-50/50 dark:bg-primary-900/10 rounded-r-lg" {...props} />,
              }}
            >
              {chunk.text}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

function TreeChildren({ node, expandedNodes, openTextNodes, toggleNode, setExpandedOnly }) {
  const id = getChunkId(node.chunk);
  const isExpanded = expandedNodes.has(id);
  if (!isExpanded) return null;
  if (!node.children || node.children.length === 0) return null;

  return (
    <div className="pl-4 sm:pl-6 space-y-2">
      {node.children.map((child) => (
        <div key={getChunkId(child.chunk)} className="rounded-xl border border-gray-200/50 dark:border-dark-700/50 overflow-hidden">
          <TreeRow node={child} expandedNodes={expandedNodes} openTextNodes={openTextNodes} toggleNode={toggleNode} setExpandedOnly={setExpandedOnly} />
          <div className="px-2 sm:px-3 pb-2 sm:pb-3">
            <TreeChildren node={child} expandedNodes={expandedNodes} openTextNodes={openTextNodes} toggleNode={toggleNode} setExpandedOnly={setExpandedOnly} />
          </div>
        </div>
      ))}
    </div>
  );
}
